/**
 * Conferência da leitura (E140.F3, GUS-143, ADR-0096 §2.7), na pré-análise eletrônica.
 *
 * O resumo diz se a leitura libera a análise; cada falha técnica vem localizada, com "ver na fonte"
 * e as duas saídas da equipe; os pontos do perito, as limitações e o que está no papel abrem por
 * expansão. Uma ação principal por falha, e o retorno de cada pedido fica na tela. Nada aqui é
 * pedido ao cliente. E147.F3: a tabela das etapas saiu daqui para a conferência das etapas, lida do
 * servidor. E155.F3: o ponto do perito com campo pede o cartão "Rever leitura na fonte" da ficha.
 */
import type { AxiosInstance } from 'axios';
import { useState } from 'react';

import { Blueprint, Button } from '@/components/ui';
import {
  falhasTecnicas,
  limitacoesRegistradas,
  pontosDoPerito,
  type ConferenciaDaLeituraOutData,
} from '@/lib/conferencia_da_leitura';
import type { PaginaNaFonte } from '@/lib/ficha_da_leitura';
import { formatarDataHora } from '@/lib/trilha_format';
import { useConferenciaDaLeitura, type UsoDaConferencia } from '@/lib/useConferenciaDaLeitura';

import { PaginaDaFonte } from '../FichaDaLeitura/PaginaDaFonte';
import { PendenciaTecnica } from './PendenciaTecnica';
import { PontosDoPerito, type ReverLeitura } from './PontosDoPerito';
import { preencher, t } from './textos';

function Resumo({ dados }: { dados: ConferenciaDaLeituraOutData }): JSX.Element {
  const chave = dados.vigente ? dados.conferencia.conferencia.estado : 'naoVigente';
  return (
    <div className="border border-accent-200 bg-accent-bg p-sc-4" data-testid="conferencia-resumo">
      <p className="text-body-md font-semibold text-fg" data-estado={chave}>
        {t.resumo[chave]}
      </p>
      <p className="mt-1 text-body-sm text-fg-muted">{t.resumoTexto[chave]}</p>
      <p className="mt-1 text-caption text-fg-muted" data-testid="conferencia-versao">
        {preencher(t.versao, {
          quando: formatarDataHora(dados.conferencia.criada_em),
          versao: dados.conferencia.versao,
          ficha: dados.conferencia.conferencia.ficha_versao,
        })}
      </p>
    </div>
  );
}

function Pronta({
  dados,
  pedir,
  onVerNaFonte,
  onReverLeitura,
  semPontos,
  consulta,
}: {
  dados: ConferenciaDaLeituraOutData;
  pedir: UsoDaConferencia['pedir'];
  onVerNaFonte: (alvo: PaginaNaFonte) => void;
  onReverLeitura: ReverLeitura | undefined;
  /** E205.F3: na mesa, os pontos do perito estão na aba Pendências. */
  semPontos: boolean;
  consulta: boolean;
}): JSX.Element {
  const [aviso, setAviso] = useState<string | null>(null);
  const conferencia = dados.conferencia.conferencia;
  const falhas = falhasTecnicas(conferencia);
  return (
    <>
      <Resumo dados={dados} />
      <p role="status" className="text-body-sm text-fg" data-testid="conferencia-aviso">
        {aviso ?? ''}
      </p>
      {falhas.length > 0 ? (
        <section aria-labelledby="conferencia-falhas-titulo" className="space-y-sc-2">
          <h4 id="conferencia-falhas-titulo" className="text-body-md font-semibold text-fg">
            {t.falhasTitulo}
          </h4>
          <p className="text-body-sm text-fg-muted">{t.falhasTexto}</p>
          <ul className="space-y-sc-2">
            {falhas.map((p) => (
              <PendenciaTecnica
                key={p.chave}
                pendencia={p}
                pedir={pedir}
                onVerNaFonte={onVerNaFonte}
                onFeito={setAviso}
                consulta={consulta}
              />
            ))}
          </ul>
        </section>
      ) : null}
      {semPontos ? null : (
        <PontosDoPerito
          perito={pontosDoPerito(conferencia)}
          limitacoes={limitacoesRegistradas(conferencia)}
          achados={conferencia.achados}
          onVerNaFonte={onVerNaFonte}
          onReverLeitura={onReverLeitura}
        />
      )}
    </>
  );
}

export interface ConferenciaDaLeituraProps {
  client: AxiosInstance;
  caseId: string;
  /** Muda quando o caso é relido (exame, documento novo): a conferência é lida de novo. */
  releitura?: unknown;
  /** O ponto do perito pede "Rever leitura de um campo": a ficha abre o cartão (E155.F3). */
  onReverLeitura?: ReverLeitura | undefined;
  /**
   * E203.F5.T03 (ADR-0146): na mesa, a conferência só aparece com pendência; sem ela (ou ainda
   * carregando, ou sem conferência) não ocupa a tela. A falha de carga continua à vista, com a nova
   * tentativa. E205.F3 (ADR-0147 §5): na mesa, só a falha técnica — os pontos do perito são
   * pendências do servidor, na aba Pendências, e saem daqui (o bloco inteiro fica em "Mais").
   */
  soComPendencia?: boolean;
  /**
   * E205.F4.C02 (F-E205.F4-02): só consulta, em "Informações da leitura": as falhas e os pontos com
   * "Ver na fonte", sem nova leitura, limitação nem "Rever leitura" (isso fica na mesa).
   */
  consulta?: boolean;
}

/** Na mesa (`soComPendencia`): a falha de carga ou a conferência com falha técnica. */
function aMostrar(conferencia: UsoDaConferencia['conferencia']): boolean {
  if (conferencia.estado === 'erro') return true;
  return (
    conferencia.estado === 'pronto' &&
    falhasTecnicas(conferencia.dados.conferencia.conferencia).length > 0
  );
}

export function ConferenciaDaLeitura({
  client,
  caseId,
  releitura,
  onReverLeitura,
  soComPendencia = false,
  consulta = false,
}: ConferenciaDaLeituraProps): JSX.Element | null {
  const { conferencia, reler, pedir } = useConferenciaDaLeitura(client, caseId, releitura);
  const [alvo, setAlvo] = useState<PaginaNaFonte | null>(null);
  if (soComPendencia && !aMostrar(conferencia)) return null;
  return (
    <Blueprint
      as="section"
      className="mt-sc-6 space-y-sc-4 p-sc-6"
      data-testid="conferencia-da-leitura"
      data-estado={conferencia.estado}
      aria-labelledby="conferencia-da-leitura-titulo"
    >
      <div>
        <h3 id="conferencia-da-leitura-titulo" className="text-body-lg text-fg">
          {t.titulo}
        </h3>
      </div>
      {conferencia.estado === 'carregando' ? (
        <p className="text-body-sm text-fg-muted">{t.carregando}</p>
      ) : null}
      {conferencia.estado === 'vazio' ? (
        <p className="text-body-sm text-fg-muted" data-testid="conferencia-vazia">
          {t.vazio}
        </p>
      ) : null}
      {conferencia.estado === 'erro' ? (
        <div role="alert" className="space-y-sc-2" data-testid="conferencia-erro">
          <p className="text-body-sm text-danger-700">
            {conferencia.motivo === 'sem_permissao' ? t.semPermissao : t.erro}
          </p>
          {conferencia.motivo === 'indisponivel' ? (
            <Button type="button" variant="secondary" size="sm" onClick={reler}>
              {t.tentarDeNovo}
            </Button>
          ) : null}
        </div>
      ) : null}
      {conferencia.estado === 'pronto' ? (
        <Pronta
          dados={conferencia.dados}
          pedir={pedir}
          onVerNaFonte={setAlvo}
          onReverLeitura={consulta ? undefined : onReverLeitura}
          semPontos={soComPendencia}
          consulta={consulta}
        />
      ) : null}
      <PaginaDaFonte client={client} caseId={caseId} alvo={alvo} onFechar={() => setAlvo(null)} />
    </Blueprint>
  );
}
